import React from "react";
import { getOneCompany } from "../../../../middlewares/sysNavigation/SysNavigation";

const View = () => {
  const company = getOneCompany();

  const hasLogo = !!company?.thumbnail;
  const hasCode = !!company?.code;
  const hasWebsite = !!company?.website;
  const addressText = company
    ? `${company?.country || "-"}${company?.city ? ` — ${company?.city}` : ""}`
    : "-";

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4 rounded-b-xl">
      <div className="mx-auto w-full max-w-5xl space-y-5">
        {/* Header */}
        <div className="relative">
          <div className="pointer-events-none absolute -inset-x-6 -top-6 h-24 rounded-3xl bg-gradient-to-r from-emerald-400/15 via-amber-400/15 to-fuchsia-400/15 blur-2xl -z-10" />
          <div className="relative rounded-2xl border border-gray-200 bg-white/90 shadow-sm backdrop-blur p-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="text-lg font-semibold tracking-tight text-gray-900">
                  {company ? company?.name : "-"}
                </h1>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <StatusChip
                    label="Code"
                    value={hasCode ? company?.code : "-"}
                  />
                  <StatusChip
                    label="Address"
                    value={addressText || "-"}
                  />
                  {hasWebsite ? (
                    <a
                      href={company?.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center rounded-full bg-gray-900 px-3 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:opacity-90"
                    >
                      Visit Website
                    </a>
                  ) : (
                    <MutedChip label="Website" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Media */}
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-3">
          <div className="rounded-2xl bg-gradient-to-br from-gray-100 via-gray-50 to-white p-[1px]">
            <figure className="overflow-hidden rounded-2xl bg-white">
              {hasLogo ? (
                <img
                  src={company?.thumbnail}
                  alt={company?.name || "Company thumbnail"}
                  className="h-48 w-full object-cover sm:h-72"
                />
              ) : (
                <div className="grid h-48 w-full place-items-center text-sm text-gray-500 sm:h-72">
                  No image available
                </div>
              )}
            </figure>
          </div>
        </section>

        {/* Details */}
        <section className="rounded-2xl border border-gray-200 bg-white shadow-sm p-5 space-y-4">
          <h2 className="text-sm font-semibold tracking-wide text-gray-800">
            Company Overview
          </h2>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field
              label="Company"
              value={company ? company?.name : "-"}
            />
            <Field
              label="Website"
              value={
                hasWebsite ? (
                  <a
                    href={company?.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-gray-300 underline-offset-4 hover:decoration-gray-500 text-gray-900"
                  >
                    {company?.website}
                  </a>
                ) : (
                  "-"
                )
              }
            />
            <Field label="Code" value={hasCode ? company?.code : "-"} />
            <Field label="Address" value={addressText || "-"} />
          </div>

          <div className="pt-2">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Description
            </h3>
            <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 text-sm leading-relaxed text-gray-800">
              {company && company?.description ? (
                <div dangerouslySetInnerHTML={{ __html: company?.description }} />
              ) : (
                <span className="text-gray-500">No description provided.</span>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

const StatusChip = ({ label, value }) => {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-800 ring-1 ring-gray-200">
      <span className="text-[10px] uppercase tracking-wide text-gray-500">{label}:</span>
      <span className="font-semibold">{typeof value === "string" ? value : String(value)}</span>
    </span>
  );
};

const MutedChip = ({ label }) => {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-500 ring-1 ring-gray-200">
      {label}: <span className="text-gray-400">-</span>
    </span>
  );
};

const Field = ({ label, value }) => {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <div className="mt-1 text-sm font-medium text-gray-900">{value}</div>
    </div>
  );
};

export default View;
